import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { AlertCircle } from "react-feather";
import { teacherStudent, addCommunity, removeCommunity } from "../../../../Api/apiUtils";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../../../../utils/formatDate";
import { toast } from "react-toastify";
import { MixpanelEvents, trackEvent } from "../../../../utils/mixpanelTracking";
import { Button, EmptyState, Field, Loader, SearchInput, Table, Toolbar } from "../../../../ui";
import type { BadgeTone } from "../../../../ui";
import TeacherStatusBadge from "./ui-pending/TeacherStatusBadge";
import "./community.scss";

/** Student categories returned by teacherStudent; active/expired are already in the shared map. */
const COMMUNITY_STATUS_TONES: Record<string, BadgeTone> = {
  paid: "success",
  engaged: "info",
  disengaged: "warning",
};

interface Student {
  studentId: number;
  studentUserId: any;
  emailId: string;
  studentName: string;
  countryCode: string;
  mobileNumber: string;
  lastClassTaken: string;
  expiryDate: string;
  studentCredentialId: string;
  enrollmentDate: any;
  courseName: any;
  courseId?: any;
  communityId: any;
  category?: string; // To track which category the student belongs to
}

interface StudentDataResponse {
  paid: { data: Student[]; count: number };
  engaged: { data: Student[]; count: number };
  active: { data: Student[]; count: number };
  disengaged: { data: Student[]; count: number };
  expired: { data: Student[]; count: number };
}

const Community: React.FC = () => {
  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [noData, setNoData] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingButtons, setLoadingButtons] = useState<{ [key: string]: boolean }>({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [actionType, setActionType] = useState<"add" | "remove" | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setErrorMessage(null);
      const storedTeacherId = localStorage.getItem("teacherProfileUserId");
      try {
        const response = await teacherStudent(storedTeacherId);

        if (!response || Object.keys(response).length === 0) {
          setNoData(true);
          setAllStudents([]);
        } else {
          setNoData(false);
          // Combine all students from all categories
          const combinedStudents: Student[] = [];
          const categories: (keyof StudentDataResponse)[] = ["paid", "engaged", "active", "disengaged", "expired"];
          
          categories.forEach((category) => {
            if (response[category]?.data) {
              response[category].data.forEach((student: Student) => {
                combinedStudents.push({
                  ...student,
                  category: category
                });
              });
            }
          });
          
          setAllStudents(combinedStudents);
        }
      } catch (error: any) {
        console.error("Error fetching student data:", error);
      
        // ✅ Handle 404 explicitly
        if (error?.response?.status === 404) {
          const apiError = "Teacher community not found";
          setErrorMessage(apiError);
          setNoData(true);
          setAllStudents([]);
          toast.info("Teacher community not found");

          setLoading(false);
          return; // ⬅️ VERY IMPORTANT
        }
      
        // ✅ Fallback for other errors
        const fallbackMessage =
          error?.response?.data?.message ||
          "Error fetching student data";
      
        setErrorMessage(fallbackMessage);
        toast.error("Teacher community not found");

        setNoData(true);
        setAllStudents([]);
      }
      
      setLoading(false);
    };

    fetchData();
  }, []);

  // Handle click on student name
  const handleCardClick = (studentCredentialId: string, studentId: number) => {
    if (studentCredentialId && studentId) {
      navigate(`/pages/student-profile/${studentCredentialId}/${studentId}`);
    } else {
      console.error("Missing studentCredentialId or studentId");
    }
  };

  // Show confirmation modal for add action
  const handleAddClick = (student: Student) => {
    setSelectedStudent(student);
    setActionType("add");
    setShowConfirmModal(true);
  };

  // Show confirmation modal for remove action
  const handleRemoveClick = (student: Student) => {
    setSelectedStudent(student);
    setActionType("remove");
    setShowConfirmModal(true);
  };

  // Handle add to community (called after confirmation)
  const handleAddToCommunity = async () => {
    if (!selectedStudent) return;

    const buttonKey = `add-${selectedStudent.studentId}`;
    setLoadingButtons(prev => ({ ...prev, [buttonKey]: true }));
    setShowConfirmModal(false);
    
    try {
      const storedTeacherId = localStorage.getItem("teacherProfileUserId");
      
      if (!storedTeacherId) {
        toast.error("Teacher ID not found");
        return;
      }

      if (!selectedStudent.courseId) {
        toast.error("Course ID not available for this student");
        return;
      }

      await addCommunity({
        studentUserId: selectedStudent.studentUserId.toString(),
        teacherUserId: storedTeacherId,
        courseId: selectedStudent.courseId.toString()
      });

      toast.success("Student added to community successfully");
      
      // Track Mixpanel event
      trackEvent(MixpanelEvents.ADD_TO_COMMUNITY, {
        studentId: selectedStudent.studentId,
        studentUserId: selectedStudent.studentUserId,
        studentName: selectedStudent.studentName,
        teacherUserId: storedTeacherId,
        courseId: selectedStudent.courseId,
        courseName: selectedStudent.courseName,
        addedAt: new Date().toISOString()
      });
      
      // Refresh the data
      const response = await teacherStudent(storedTeacherId);
      if (response && Object.keys(response).length > 0) {
        const combinedStudents: Student[] = [];
        const categories: (keyof StudentDataResponse)[] = ["paid", "engaged", "active", "disengaged", "expired"];
        
        categories.forEach((category) => {
          if (response[category]?.data) {
            response[category].data.forEach((student: Student) => {
              combinedStudents.push({
                ...student,
                category: category
              });
            });
          }
        });
        
        setAllStudents(combinedStudents);
      }
    } catch (error: any) {
      console.error("Error adding student to community:", error);
      toast.info("Teacher community not found");

    } finally {
      setLoadingButtons(prev => ({ ...prev, [buttonKey]: false }));
      setSelectedStudent(null);
      setActionType(null);
    }
  };

  // Handle remove from community (called after confirmation)
  const handleRemoveFromCommunity = async () => {
    if (!selectedStudent) return;

    const buttonKey = `remove-${selectedStudent.studentId}`;
    setLoadingButtons(prev => ({ ...prev, [buttonKey]: true }));
    setShowConfirmModal(false);
    
    try {
      const storedTeacherId = localStorage.getItem("teacherProfileUserId");
      
      if (!selectedStudent.courseId) {
        toast.error("Course ID not available for this student");
        return;
      }

      await removeCommunity({
        studentUserId: selectedStudent.studentUserId.toString(),
        courseId: selectedStudent.courseId.toString()
      });

      toast.success("Student removed from community successfully");
      
      // Track Mixpanel event
      trackEvent(MixpanelEvents.REMOVE_FROM_COMMUNITY, {
        studentId: selectedStudent.studentId,
        studentUserId: selectedStudent.studentUserId,
        studentName: selectedStudent.studentName,
        teacherUserId: storedTeacherId,
        courseId: selectedStudent.courseId,
        courseName: selectedStudent.courseName,
        removedAt: new Date().toISOString()
      });
      
      // Refresh the data
      if (storedTeacherId) {
        const response = await teacherStudent(storedTeacherId);
        if (response && Object.keys(response).length > 0) {
          const combinedStudents: Student[] = [];
          const categories: (keyof StudentDataResponse)[] = ["paid", "engaged", "active", "disengaged", "expired"];
          
          categories.forEach((category) => {
            if (response[category]?.data) {
              response[category].data.forEach((student: Student) => {
                combinedStudents.push({
                  ...student,
                  category: category
                });
              });
            }
          });
          
          setAllStudents(combinedStudents);
        }
      }
    } catch (error: any) {
      console.error("Error removing student from community:", error);
      const errorMessage = error.response?.data?.message || error.message || "Error removing student from community";
      toast.error(errorMessage);
    } finally {
      setLoadingButtons(prev => ({ ...prev, [buttonKey]: false }));
      setSelectedStudent(null);
      setActionType(null);
    }
  };

  // Toggle confirmation modal
  const toggleConfirmModal = () => {
    setShowConfirmModal(!showConfirmModal);
    if (!showConfirmModal) {
      setSelectedStudent(null);
      setActionType(null);
    }
  };

  // Filter students based on search query
  const filterStudents = (students: Student[]) => {
    if (!searchQuery) return students;

    const lowerCaseQuery = searchQuery.toLowerCase();
    return students.filter(
      (student) =>
        student.studentName.toLowerCase().includes(lowerCaseQuery) ||
        student.emailId.toLowerCase().includes(lowerCaseQuery) ||
        student.mobileNumber.toLowerCase().includes(lowerCaseQuery) ||
        student.courseName.toLowerCase().includes(lowerCaseQuery)
    );
  };

  // Get status label based on category
  const getStatusLabel = (category?: string) => {
    switch (category) {
      case "paid":
        return "Paid";
      case "engaged":
        return "Engaged";
      case "active":
        return "Active";
      case "disengaged":
        return "Disengaged";
      case "expired":
        return "Expired";
      default:
        return "Unknown";
    }
  };

  if (loading) {
    return <Loader label="Loading community students…" />;
  }

  if (noData) {
    return errorMessage ? (
      <EmptyState
        icon={<AlertCircle size={22} aria-hidden />}
        title={errorMessage}
        description="This teacher may not have a community set up yet."
      />
    ) : (
      <EmptyState title="No data found" description="No students were returned for this teacher." />
    );
  }

  const filteredStudents = filterStudents(allStudents);

  return (
    <div className="tp-community">
      {/* Search Bar */}
      <Toolbar>
        <Field label="Search students" className="ds-toolbar__grow">
          {(control) => (
            <SearchInput
              {...control}
              placeholder="Search by name, email, phone number or course name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          )}
        </Field>
      </Toolbar>

      {/* Table */}
      {filteredStudents.length ? (
        <div className="tp-community__scroll">
          <Table caption="Community students">
            <thead>
              <tr>
                <th>Student Name</th>
                <th>Course Name</th>
                <th>Status</th>
                {/* <th>Email</th> */}
                {/* <th>Mobile Number</th> */}
                {/* <th>Last Class Taken</th> */}
                {/* <th>Enrollment Date</th> */}
                <th className="tp-community__action-col">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map((student) => (
                <tr key={`${student.studentId}-${student.category}`}>
                  <td>
                    <button
                      type="button"
                      className="tp-community__name"
                      onClick={() => handleCardClick(student.studentCredentialId, student.studentId)}
                    >
                      {student.studentName}
                    </button>
                  </td>
                  <td>{student.courseName}</td>
                  <td>
                    <TeacherStatusBadge
                      status={student.category}
                      label={getStatusLabel(student.category)}
                      tones={COMMUNITY_STATUS_TONES}
                    />
                  </td>
                  {/* <td>{student.emailId}</td> */}
                  {/* <td>{student.countryCode} {student.mobileNumber}</td> */}
                  {/* <td>{student.lastClassTaken}</td> */}
                  {/* <td>{formatDate(student.enrollmentDate, "dateTime")}</td> */}
                  <td className="tp-community__action-col">
                    {student.communityId ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="tp-community__remove"
                        onClick={() => handleRemoveClick(student)}
                        disabled={loadingButtons[`remove-${student.studentId}`]}
                      >
                        {loadingButtons[`remove-${student.studentId}`] ? "Removing..." : "Remove"}
                      </Button>
                    ) : (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleAddClick(student)}
                        disabled={loadingButtons[`add-${student.studentId}`]}
                      >
                        {loadingButtons[`add-${student.studentId}`] ? "Adding..." : "Add"}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : (
        <EmptyState
          title="No student data available"
          description={searchQuery ? "No students match your search. Try a different name, email, phone or course." : undefined}
        />
      )}

      {/* Confirmation Modal */}
      <Modal isOpen={showConfirmModal} toggle={toggleConfirmModal} centered>
        <ModalHeader toggle={toggleConfirmModal}>
          {actionType === "add" ? "Add to Community" : "Remove from Community"}
        </ModalHeader>
        <ModalBody>
          <p className="tp-community__confirm-text">
            Are you sure you want to {actionType === "add" ? "add" : "remove"} <strong>{selectedStudent?.studentName}</strong> {actionType === "add" ? "to" : "from"} the community?
          </p>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={toggleConfirmModal}>
            Cancel
          </Button>
          <Button
            variant={actionType === "add" ? "primary" : "danger"}
            onClick={actionType === "add" ? handleAddToCommunity : handleRemoveFromCommunity}
            disabled={loadingButtons[actionType === "add" ? `add-${selectedStudent?.studentId}` : `remove-${selectedStudent?.studentId}`]}
          >
            {actionType === "add" ? "Add" : "Remove"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Community;
